import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { WkoHighchart } from "../WkoHighchart";
const modalId = "ncPerHeadOutliersModal";
const formatNumber = (value) => {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
const mapOutliers = (outliers) => {
    return (outliers ?? [])
        .map((outlier) => ({
        vesselName: outlier.VesselName,
        ncsPerHead: outlier.NcsPerHead,
    }))
        .sort((a, b) => b.ncsPerHead - a.ncsPerHead);
};
const getChartOptions = (result) => ({
    chart: {
        type: "boxplot",
        height: 220,
        inverted: true,
    },
    exporting: { enabled: false },
    legend: { enabled: false },
    xAxis: {
        categories: ["Fleet"],
        visible: false,
    },
    yAxis: {
        title: { text: "" },
    },
    plotOptions: {
        boxplot: {
            fillColor: "rgba(69, 114, 160, 0.15)",
            medianColor: "#b5213c",
            medianWidth: 3,
            stemColor: "#636b73",
            stemWidth: 1,
            whiskerColor: "#636b73",
            whiskerWidth: 2,
            whiskerLength: "50%",
        },
    },
    tooltip: {
        headerFormat: "",
    },
    series: [
        {
            type: "boxplot",
            name: "NC per Head",
            data: [[result.Min, result.Q1, result.Median, result.Q3, result.Max]],
        },
    ],
});
export function NcDaysPerCrewMemberBoxplot({ options }) {
    const [isFetching, setIsFetching] = useState(false);
    const [hasResult, setHasResult] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [result, setResult] = useState(null);
    const isLoading = !options.isReady || isFetching;
    const outliers = mapOutliers(result?.Outliers);
    useEffect(() => {
        if (!options.isReady) {
            setHasResult(false);
            setIsFetching(false);
            setErrorMessage("");
            setResult(null);
            return;
        }
        setIsFetching(true);
        setHasResult(false);
        setErrorMessage("");
        setResult(null);
        const params = new URLSearchParams();
        params.append("year", String(options.year));
        params.append("quarter", String(options.quarter));
        options.fleetIds.forEach((id) => params.append("fleetIds", String(id)));
        fetch(`/api/insights/workload/ncperhead?${params}`)
            .then((res) => {
            if (res.status === 204)
                return null;
            if (res.ok)
                return res.json();
            throw new Error(res.statusText);
        })
            .then((data) => {
            setResult(data);
            setHasResult(true);
        })
            .catch((error) => {
            console.error(error);
            setErrorMessage("Error fetching NC per head data");
        })
            .finally(() => setIsFetching(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.isReady, options.year, options.quarter, options.fleetIds.join(",")]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "card shadow h-100", children: [_jsx("h3", { className: "card-header", children: "NC Days per Crew Member" }), _jsxs("div", { className: "card-body d-flex flex-column", children: [_jsx("p", { className: "text-muted small mb-0", children: "Shows for each vessel the average number of NCs per crew over the period." }), _jsx("p", { className: "text-muted small mb-0", children: "How to read this chart: This is a distribution of all the selected vessels, showing for each of them what their average NC day per crew member was over the period. Each value represents one vessel." }), isLoading && (_jsxs("div", { className: "text-center py-4", children: [_jsx("span", { className: "spinner-grow spinner-grow-sm", role: "status", "aria-hidden": "true" }), " Loading..."] })), options.isReady && errorMessage && (_jsx("div", { className: "alert alert-danger", role: "alert", children: errorMessage })), hasResult && !isLoading && result !== null && (_jsxs(_Fragment, { children: [_jsx("div", { style: { minHeight: "200px" }, children: _jsx(WkoHighchart, { options: getChartOptions(result) }) }), _jsxs("p", { className: "text-muted mb-0 mt-auto", children: ["Median: ", _jsx("span", { children: formatNumber(result.Median) }), " \u00B7 ", _jsx("span", { children: result.VesselCount }), " vessels"] })] })), hasResult && !isLoading && outliers.length > 0 && (_jsx("p", { className: "text-muted mb-0 mt-2 small", children: _jsxs("a", { href: `#${modalId}`, "data-bs-toggle": "modal", "data-bs-target": `#${modalId}`, children: [outliers.length, " outlier(s) excluded >"] }) })), hasResult && !isLoading && result === null && (_jsx("p", { className: "text-muted mb-0", children: "No data available for this period." }))] })] }), _jsx("div", { id: modalId, className: "modal fade", tabIndex: -1, role: "dialog", "aria-hidden": "true", "aria-labelledby": "ncPerHeadOutliersModalTitle", children: _jsx("div", { className: "modal-dialog modal-dialog-scrollable", role: "document", children: _jsxs("div", { className: "modal-content", children: [_jsxs("div", { className: "modal-header", children: [_jsxs("h5", { id: "ncPerHeadOutliersModalTitle", className: "modal-title", children: ["Outliers excluded from chart (", outliers.length, ")"] }), _jsx("button", { type: "button", className: "btn-close", "data-bs-dismiss": "modal", "aria-label": "Close" })] }), _jsx("div", { className: "modal-body", children: _jsxs("div", { className: "list-group", children: [_jsxs("div", { className: "list-group-item d-flex justify-content-between py-2", children: [_jsx("span", { children: "Vessel" }), _jsx("span", { children: "NCs per Crew" })] }), outliers.map((outlier) => (_jsxs("div", { className: "list-group-item d-flex justify-content-between py-2", children: [_jsx("span", { children: outlier.vesselName }), _jsx("span", { children: formatNumber(outlier.ncsPerHead) })] }, `${outlier.vesselName}-${outlier.ncsPerHead}`)))] }) }), _jsx("div", { className: "modal-footer", children: _jsx("button", { type: "button", className: "btn btn-secondary", "data-bs-dismiss": "modal", children: "Close" }) })] }) }) })] }));
}
